import { useCallback, useEffect, useRef, useState } from 'react'
import {
  dismissForever,
  recordImpression,
  registerVisit,
  shouldShowPrompt,
} from '../utils/extensionPrompt'

export interface ExtensionPrompt {
  visible: boolean
  /** The user said no. Hide it now and never ask again. */
  dismiss: () => void
  /**
   * The user went to the store. Records the answer but deliberately leaves the
   * banner mounted: unmounting a target="_blank" anchor inside its own click
   * handler can cancel the navigation, because React flushes discrete-event
   * state updates before the browser processes the default action.
   */
  acknowledge: () => void
}

/**
 * Decides once per mount whether to show the extension prompt, and counts the
 * visit while it is at it. See utils/extensionPrompt for the rules.
 */
export function useExtensionPrompt(): ExtensionPrompt {
  const [visible, setVisible] = useState(false)
  // StrictMode mounts effects twice in development. The visit counter is already
  // idempotent per session, but the impression counter is not, and a ref survives
  // the remount where local state does not.
  const decided = useRef(false)

  useEffect(() => {
    if (decided.current) return
    decided.current = true

    const visits = registerVisit()
    if (shouldShowPrompt(visits)) {
      recordImpression()
      setVisible(true)
    }
  }, [])

  const dismiss = useCallback(() => {
    dismissForever()
    setVisible(false)
  }, [])

  const acknowledge = useCallback(() => {
    dismissForever()
  }, [])

  return { visible, dismiss, acknowledge }
}
